import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import DealsAPI from '../../api/deals';

export const state = {
  records: [],
  currentPipelineId: null,
  dashboardStats: {},
  pipelineMetrics: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getDeals(_state) {
    return _state.records;
  },
  getCurrentPipelineId(_state) {
    return _state.currentPipelineId;
  },
  getDashboardStats(_state) {
    return _state.dashboardStats;
  },
  getPipelineMetrics(_state) {
    return _state.pipelineMetrics;
  },
  getItemsByStage: _state => stageIdOrKey => {
    return _state.records
      .filter(
        record =>
          record.current_stage_id === stageIdOrKey ||
          record.funnel_stage === stageIdOrKey
      )
      .sort((a, b) => a.position - b.position);
  },
  getItemsGroupedByStage(_state) {
    return _state.records.reduce((grouped, item) => {
      const stage = item.current_stage_id || item.funnel_stage;
      if (!grouped[stage]) {
        grouped[stage] = [];
      }
      grouped[stage].push(item);
      return grouped;
    }, {});
  },
};

export const actions = {
  get: async function getDeals({ commit }) {
    commit(types.SET_DEAL_UI_FLAG, { isFetching: true });
    try {
      const response = await DealsAPI.get();
      commit(types.SET_DEALS, response.data.payload);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isFetching: false });
    }
  },
  getAll: async function getAllDeals({ commit }, params = {}) {
    commit(types.SET_DEAL_UI_FLAG, { isFetching: true });
    try {
      const response = await DealsAPI.getAll({ lite: true, ...params });
      commit(types.SET_DEALS, response.data.payload);
      commit(
        types.SET_DEAL_PIPELINE_METRICS,
        response.data.meta?.metrics || null
      );
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isFetching: false });
    }
  },
  getByPipeline: async function getDealsByPipeline(
    { commit, dispatch },
    { pipelineId, params }
  ) {
    commit(types.SET_DEAL_UI_FLAG, { isFetching: true });
    try {
      const response = await DealsAPI.getByPipeline(pipelineId, params);
      commit(types.SET_DEALS, response.data.payload);
      commit(types.SET_CURRENT_PIPELINE, pipelineId);
      commit(
        types.SET_DEAL_PIPELINE_METRICS,
        response.data.meta?.metrics || null
      );
      dispatch('fetchThumbnails');
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isFetching: false });
    }
  },
  fetchThumbnails: async function fetchThumbnails({ commit, state: _state }) {
    // Guard against concurrent calls
    if (_state.uiFlags.isFetchingThumbnails) return;
    commit(types.SET_DEAL_UI_FLAG, { isFetchingThumbnails: true });

    const displayIds = _state.records
      .map(r => r.conversation_display_id)
      .filter(Boolean);
    if (displayIds.length === 0) {
      commit(types.SET_DEAL_UI_FLAG, { isFetchingThumbnails: false });
      return;
    }

    // Single request with all IDs (server handles it efficiently)
    try {
      const response = await DealsAPI.getThumbnails(displayIds);
      const thumbnails = response.data;
      if (Object.keys(thumbnails).length > 0) {
        commit(types.SET_DEAL_THUMBNAILS, thumbnails);
      }
    } catch (error) {
      // Silently ignore thumbnail errors
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isFetchingThumbnails: false });
    }
  },
  show: async function showDeal({ commit, state: _state }, { id }) {
    commit(types.SET_DEAL_UI_FLAG, { isFetching: true });
    try {
      const response = await DealsAPI.show(id);
      const exists = _state.records.some(r => r.id === response.data.id);
      if (exists) {
        commit(types.EDIT_DEAL, response.data);
      } else {
        commit(types.ADD_DEAL, response.data);
      }
      return response.data;
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createDeal({ commit }, itemObj) {
    commit(types.SET_DEAL_UI_FLAG, { isCreating: true });
    try {
      const response = await DealsAPI.create(itemObj);
      commit(types.ADD_DEAL, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.update(id, updateObj);
      commit(types.EDIT_DEAL, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, { pipelineId, dealId }) => {
    commit(types.SET_DEAL_UI_FLAG, { isDeleting: true });
    try {
      await DealsAPI.destroy(pipelineId, dealId);
      commit(types.DELETE_DEAL, dealId);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isDeleting: false });
    }
  },
  moveStage: async (
    { commit },
    {
      pipelineId,
      dealId,
      stageId,
      funnelStage,
      fromStage,
      position,
      // Legacy compat
      funnelId,
      itemId,
    }
  ) => {
    const pid = pipelineId || funnelId;
    const did = dealId || itemId;

    commit(types.EDIT_DEAL, {
      id: did,
      current_stage_id: stageId,
      funnel_stage: funnelStage,
      position,
    });
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const data = stageId
        ? { stage_id: stageId, position }
        : { funnel_stage: funnelStage, position };
      const response = await DealsAPI.moveStage(pid, did, data);
      commit(types.EDIT_DEAL, response.data);
    } catch (error) {
      commit(types.EDIT_DEAL, {
        id: did,
        funnel_stage: fromStage,
        position: 0,
      });
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  movePipeline: async (
    { commit },
    { pipelineId, dealId, newPipelineId, newStageId, position }
  ) => {
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.move(pipelineId, dealId, {
        pipeline_id: newPipelineId,
        stage_id: newStageId,
        position,
      });
      commit(types.EDIT_DEAL, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  changeStatus: async (
    { commit },
    {
      pipelineId,
      dealId,
      status,
      reason,
      closeReasonId,
      // Legacy compat
      funnelId,
      itemId,
    }
  ) => {
    const pid = pipelineId || funnelId;
    const did = dealId || itemId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.changeStatus(pid, did, {
        status,
        reason,
        close_reason_id: closeReasonId,
      });
      commit(types.EDIT_DEAL, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  toggleTimer: async ({ commit }, { pipelineId, dealId, funnelId, itemId }) => {
    const pid = pipelineId || funnelId;
    const did = dealId || itemId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.toggleTimer(pid, did);
      commit(types.EDIT_DEAL, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  createNote: async (
    { commit },
    { pipelineId, dealId, text, funnelId, itemId }
  ) => {
    const pid = pipelineId || funnelId;
    const did = dealId || itemId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.createNote(pid, did, { text });
      commit(types.EDIT_DEAL, response.data);
      return response.data.note;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  createChecklistItem: async (
    { commit },
    { pipelineId, dealId, text, funnelId, itemId }
  ) => {
    const pid = pipelineId || funnelId;
    const did = dealId || itemId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.createChecklistItem(pid, did, { text });
      commit(types.EDIT_DEAL, response.data);
      return response.data.checklist_item;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  toggleChecklistItem: async (
    { commit },
    { pipelineId, dealId, checklistItemId, funnelId, itemId }
  ) => {
    const pid = pipelineId || funnelId;
    const did = dealId || itemId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.toggleChecklistItem(
        pid,
        did,
        checklistItemId
      );
      commit(types.EDIT_DEAL, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  assignAgentToChecklistItem: async (
    { commit },
    { pipelineId, dealId, checklistItemId, agentId, funnelId, itemId }
  ) => {
    const pid = pipelineId || funnelId;
    const did = dealId || itemId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.assignAgentToChecklistItem(
        pid,
        did,
        checklistItemId,
        agentId
      );
      commit(types.EDIT_DEAL, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  removeAgentFromChecklistItem: async (
    { commit },
    { pipelineId, dealId, checklistItemId, funnelId, itemId }
  ) => {
    const pid = pipelineId || funnelId;
    const did = dealId || itemId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.removeAgentFromChecklistItem(
        pid,
        did,
        checklistItemId
      );
      commit(types.EDIT_DEAL, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  assignAgent: async (
    { commit },
    { pipelineId, dealId, agentId, funnelId, itemId }
  ) => {
    const pid = pipelineId || funnelId;
    const did = dealId || itemId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.assignAgent(pid, did, agentId);
      commit(types.EDIT_DEAL, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  removeAgent: async (
    { commit },
    { pipelineId, dealId, agentId, funnelId, itemId }
  ) => {
    const pid = pipelineId || funnelId;
    const did = dealId || itemId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.removeAgent(pid, did, agentId);
      commit(types.EDIT_DEAL, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  bulkMoveItems: async (
    { commit },
    { pipelineId, itemIds, stageId, funnelStage, funnelId }
  ) => {
    const pid = pipelineId || funnelId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const data = stageId
        ? { item_ids: itemIds, stage_id: stageId }
        : { item_ids: itemIds, funnel_stage: funnelStage };
      await DealsAPI.bulkMoveItems(pid, data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  bulkAssignAgent: async (
    { commit },
    { pipelineId, itemIds, agentId, funnelId }
  ) => {
    const pid = pipelineId || funnelId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      await DealsAPI.bulkAssignAgent(pid, {
        item_ids: itemIds,
        agent_id: agentId,
      });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  bulkSetPriority: async (
    { commit },
    { pipelineId, itemIds, priority, funnelId }
  ) => {
    const pid = pipelineId || funnelId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      await DealsAPI.bulkSetPriority(pid, {
        item_ids: itemIds,
        priority,
      });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  addContact: async (
    { commit },
    { pipelineId, dealId, contactId, funnelId, itemId }
  ) => {
    const pid = pipelineId || funnelId;
    const did = dealId || itemId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.addContact(pid, did, contactId);
      commit(types.EDIT_DEAL, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  removeContact: async (
    { commit },
    { pipelineId, dealId, contactId, funnelId, itemId }
  ) => {
    const pid = pipelineId || funnelId;
    const did = dealId || itemId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.removeContact(pid, did, contactId);
      commit(types.EDIT_DEAL, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  reorder: async ({ commit }, { pipelineId, items, funnelId }) => {
    const pid = pipelineId || funnelId;
    commit(types.SET_DEAL_UI_FLAG, { isUpdating: true });
    try {
      const response = await DealsAPI.reorder(pid, items);
      commit(types.SET_DEALS, response.data.payload);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_DEAL_UI_FLAG, { isUpdating: false });
    }
  },
  fetchDashboardStats: async function fetchDashboardStats({ commit }) {
    const response = await DealsAPI.getDashboardStats();
    commit(types.SET_DEAL_DASHBOARD_STATS, response.data);
  },
  setCurrentPipeline: ({ commit }, pipelineId) => {
    commit(types.SET_CURRENT_PIPELINE, pipelineId);
  },
  resetStore({ commit }) {
    commit('RESET_DEALS_STORE');
  },
};

export const mutations = {
  [types.SET_DEAL_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.SET_CURRENT_PIPELINE](_state, pipelineId) {
    _state.currentPipelineId = pipelineId;
  },

  [types.ADD_DEAL]: MutationHelpers.create,
  [types.SET_DEALS]: MutationHelpers.set,
  [types.EDIT_DEAL]: MutationHelpers.updateAttributes,
  [types.DELETE_DEAL]: MutationHelpers.destroy,
  [types.SET_DEAL_DASHBOARD_STATS](_state, data) {
    _state.dashboardStats = data;
  },
  [types.SET_DEAL_PIPELINE_METRICS](_state, data) {
    _state.pipelineMetrics = data;
  },
  RESET_DEALS_STORE(_state) {
    _state.records = [];
    _state.currentPipelineId = null;
    _state.dashboardStats = {};
    _state.pipelineMetrics = null;
    _state.uiFlags = {
      isFetching: false,
      isCreating: false,
      isUpdating: false,
      isDeleting: false,
    };
  },
  // Merge avatar_url thumbnails into existing items
  [types.SET_DEAL_THUMBNAILS](_state, thumbnails) {
    _state.records.forEach(record => {
      const url = thumbnails[record.conversation_display_id];
      if (url) {
        record.avatar_url = url;
      }
    });
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
